import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import {
  useNavigate,
} from 'react-router';
import Grid from '@mui/material/Grid';
import {
  AdProviderHelper,
  ProgrammaticBuyerHelper,
  Field2 as Field,
  viArrowOutward,
  color,
} from 'vibeguide';
import './OrderInformation.scss';

function OrderInformation({
  className = '',
  style = {},
  orderType = '',
  opportunityName = '',
  opportunityId = '',
  dealId = '',
  extReferenceId = '',
  agency = '',
  category = '',
  deliverySystem = '',
  adProvider = '',
  advertiserName = '',
  programmaticBuyer = '',
  orderStartDate = '',
  orderEndDate = '',
  orderCancellationDate = '',
  orderStatus = '',
}) {
  const history = useNavigate();

  const showDealId = orderType === 'programmatic' || orderType === 'always-on-pmp';
  const isOpenExchange = orderType === 'open-exchange';

  const titleStyle = {
    marginBottom: 16,
    fontSize: 16,
    fontWeight: 'bold',
    color: color.secondary,
  };

  /**
   * When the user clicks to view all orders with an Opportunity ID
   */
  const onClickCustomButtonOpportunityId = () => {
    history(`/ad-ops/orders/all?opportunityId=${opportunityId}`);
  };

  /**
   * When the user clicks to view all orders with a Deal ID
   */
  const onClickCustomButtonDealId = () => {
    history(`/ad-ops/orders/all?dealId=${dealId}`);
  };

  return (
    <div
      className={classNames('OrderInformation', className)}
      style={style}
    >
      {!isOpenExchange && (
        <div className="info-container">
          <div
            style={titleStyle}
          >
            Tracking &amp; Reporting
          </div>

          <Grid spacing={2} container>
            <Grid size={12}>
              <Field
                type="text"
                label="Opportunity Name"
                name="opportunityName"
                placeholder="Opportunity Name"
                value={opportunityName}
                testId="order-opportunity-name-input"
                disabled
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="text"
                textProps={{
                  customButton: {
                    icon: viArrowOutward,
                    tooltip: opportunityId
                      ? 'View all orders with this Opportunity ID'
                      : '',
                    disabled: !opportunityId,
                    onClick: onClickCustomButtonOpportunityId,
                  },
                  helpText: 'Multiple orders can be tied to one Opportunity ID.',
                  helpTextStyle: {
                    color: color.gray500,
                  },
                }}
                label="Opportunity Salesforce ID"
                name="opportunityId"
                placeholder="Opportunity ID"
                value={opportunityId}
                testId="order-opportunity-id-input"
                disabled
              />
            </Grid>

            {showDealId && (
              <Grid size={6}>
                <Field
                  type="text"
                  textProps={{
                    customButton: {
                      icon: viArrowOutward,
                      tooltip: dealId
                        ? 'View all orders with this Deal ID'
                        : '',
                      disabled: !dealId,
                      onClick: onClickCustomButtonDealId,
                    },
                    helpText: 'Multiple orders can be tied to one Deal ID.',
                    helpTextStyle: {
                      color: color.gray500,
                    },
                  }}
                  label="Deal ID"
                  name="dealId"
                  placeholder="Deal ID"
                  value={dealId}
                  testId="order-deal-id-input"
                  disabled
                />
              </Grid>
            )}

            <Grid size={6}>
              <Field
                type="text"
                label="Order Reference ID"
                name="extReferenceId"
                placeholder="Order Reference ID"
                value={extReferenceId}
                testId="order-reference-id-input"
                disabled
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="text"
                label="Agency"
                name="agency"
                placeholder="Agency"
                value={agency}
                testId="order-agency-input"
                disabled
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="text"
                label="Ad Category"
                name="category"
                placeholder="Category"
                value={category}
                testId="order-category-input"
                disabled
              />
            </Grid>
          </Grid>
        </div>
      )}

      <div className="info-container">
        <div
          style={titleStyle}
        >
          Delivery
        </div>

        <Grid spacing={2} container>
          {!isOpenExchange && (
            <>
            <Grid size={6}>
              <Field
                type="datepicker"
                label="Start Date"
                placeholder="YYYY-MM-DD"
                value={orderStartDate}
                style={{
                  width: '100%',
                }}
                dateProps={{
                  format: 'YYYY-MM-DD',
                  tooltipDate: moment(orderStartDate)
                    .startOf('day')
                    .format('YYYY-MM-DD [at] HH:mm:ss'),
                }}
                testId="order-start-date-input"
                disabled
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="datepicker"
                label="End Date"
                placeholder="YYYY-MM-DD"
                value={orderEndDate}
                dateProps={{
                  format: 'YYYY-MM-DD',
                  tooltipDate: moment(orderEndDate)
                    .subtract(1, 'second')
                    .format('YYYY-MM-DD [at] HH:mm:ss'),
                }}
                testId="order-end-date-input"
                disabled
              />
            </Grid>
            </>
          )}

          {!isEmpty(orderCancellationDate) && (
            <Grid size={12}>
              <div
                style={{ width: 'calc(50% - 8px)' }}
              >
                <Field
                  type="datepicker"
                  label="Cancelled Date"
                  placeholder={orderStatus === 'cancelled'
                    ? 'YYYY-MM-DD'
                    : 'N/A'}
                  value={orderStatus === 'cancelled'
                    ? moment(orderCancellationDate).format('YYYY-MM-DD')
                    : null}
                  dateProps={{
                    format: 'YYYY-MM-DD',
                  }}
                  testId="order-cancelled-date-input"
                  disabled
                />
              </div>
            </Grid>
          )}

          <Grid size={6}>
            <Field
              type="dropdown"
              label="Delivery System"
              name="deliverySystem"
              value={deliverySystem}
              dropdownProps={{
                type: 'delivery system',
                attr: 'value',
              }}
              testId="order-delivery-system-dropdown"
              disabled
            />
          </Grid>

          <Grid size={6}>
            <Field
              type="select"
              label="Ad Provider"
              name="adProvider"
              value={adProvider}
              options={AdProviderHelper.list({ includeEmpty: true })}
              testId="order-ad-provider-select"
              disabled
            />
          </Grid>

          {!isOpenExchange && (
            <>
            <Grid size={6}>
              <Field
                type="dropdown"
                label="Advertiser"
                name="advertiser"
                value={advertiserName}
                dropdownProps={{
                  type: 'advertiser',
                  attr: 'name',
                }}
                testId="order-advertiser-dropdown"
                disabled
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="select"
                label="Programmatic Buyer"
                name="programmaticBuyer"
                value={programmaticBuyer}
                options={ProgrammaticBuyerHelper.list({ includeEmpty: true })}
                testId="order-programmatic-buyer-select"
                disabled
              />
            </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
}

OrderInformation.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  orderType: PropTypes.string,
  opportunityName: PropTypes.string,
  opportunityId: PropTypes.string,
  dealId: PropTypes.string,
  extReferenceId: PropTypes.string,
  agency: PropTypes.string,
  deliverySystem: PropTypes.string,
  adProvider: PropTypes.string,
  advertiserName: PropTypes.string,
  programmaticBuyer: PropTypes.string,
  orderStartDate: PropTypes.string,
  orderEndDate: PropTypes.string,
  orderCancellationDate: PropTypes.string,
  orderStatus: PropTypes.string,
};

export default OrderInformation;
